import { useEffect, useRef, useState } from 'react';
import { Icon, TextInput } from '@/components/ui';

/** Toggle chip for a select/toggle filter. Applies immediately. */
export function FilterChip({
  label,
  on,
  onToggle,
}: {
  label: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button type="button" className="chip" data-on={on} aria-pressed={on} onClick={onToggle}>
      {on ? <Icon name="check" size={12} /> : null}
      {label}
    </button>
  );
}

/** Filters the spec lists but the API contract does not support yet. Shown, never emulated client side. */
export function DeferredFilter({ label }: { label: string }) {
  return (
    <span className="chip chip-static" aria-disabled="true" title="Deferred: no API contract yet">
      {label}
      <span className="visually-hidden">, deferred: no API contract yet</span>
    </span>
  );
}

/**
 * Free text search: debounced 300 ms, Enter commits immediately (docs/ux.md 3.1).
 * `scope` names what the server searches.
 */
export function SearchInput({
  value,
  onCommit,
  scope,
  maxLength = 200,
}: {
  value: string;
  onCommit: (q: string) => void;
  scope: string;
  maxLength?: number;
}) {
  const [text, setText] = useState(value);
  const committed = useRef(value);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setText(value);
  }
  useEffect(() => {
    committed.current = value;
  }, [value]);
  useEffect(() => {
    if (text === committed.current) return;
    const t = window.setTimeout(() => {
      committed.current = text;
      onCommit(text);
    }, 300);
    return () => window.clearTimeout(t);
  }, [text, onCommit]);
  return (
    <div style={{ position: 'relative' }} className="dt-search">
      <span style={{ position: 'absolute', left: 9, top: 8, color: 'var(--text-faint)' }} aria-hidden>
        <Icon name="search" size={15} />
      </span>
      <TextInput
        type="search"
        aria-label={`Search ${scope}`}
        placeholder={`Search ${scope}`}
        value={text}
        maxLength={maxLength}
        style={{ paddingLeft: 30 }}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            committed.current = text;
            onCommit(text);
          }
        }}
      />
    </div>
  );
}
